import { ApiResponse } from '@lunit/shared-types';
import toast from 'react-hot-toast';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// Событие протухшей сессии: client.ts кидает его на 401, AuthContext ловит и уходит в anonymous.
// Кросс-модульная развязка без импорта AuthContext в client.
export const AUTH_EXPIRED_EVENT = 'auth:expired';

class ApiError extends Error {
  constructor(
    message: string,
    public code: string,
    public status?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends RequestInit {
  silent?: boolean;
}

/**
 * Get venue slug from current URL path (/:venueSlug/*)
 */
function getVenueSlugFromPath(): string {
  const segments = window.location.pathname.split('/').filter(Boolean);
  return segments[0] || '';
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const { silent = false, ...fetchOptions } = options;

  const venueSlug = getVenueSlugFromPath();

  try {
    const response = await fetch(url, {
      // Сессия — httpOnly-cookie customerSession (не Bearer-в-localStorage: XSS-устойчиво). Кросс-origin
      // pwa.lunitloyalty.tech → api.lunitloyalty.tech: credentials:'include' + CORS allow-credentials на API.
      credentials: 'include',
      ...fetchOptions,
      headers: {
        'Content-Type': 'application/json',
        'X-Venue-Slug': venueSlug,
        ...fetchOptions.headers,
      },
    });

    // Протухшая/отсутствующая сессия — уводим приложение в anonymous (guard редиректит на /login).
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
    }

    let data: ApiResponse<T>;
    try {
      data = await response.json();
    } catch {
      const errorMessage = 'Неверный формат ответа от сервера.';
      if (!silent) {
        toast.error(errorMessage);
      }
      throw new ApiError(errorMessage, 'JSON_PARSE_ERROR', response.status);
    }

    if (!data.success || !response.ok) {
      const errorMessage = data.error?.message || 'Что-то пошло не так. Попробуйте снова.';
      const errorCode = data.error?.code || 'UNKNOWN_ERROR';

      // На 401 не спамим тостом (обычная ситуация «сессия истекла»); guard покажет login.
      if (!silent && response.status !== 401) {
        toast.error(errorMessage);
      }

      throw new ApiError(errorMessage, errorCode, response.status);
    }

    return data.data as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    const errorMessage = error instanceof Error && error.message.includes('fetch')
      ? 'Ошибка сети. Проверьте подключение к интернету.'
      : 'Что-то пошло не так. Попробуйте снова.';

    if (!silent) {
      toast.error(errorMessage);
    }

    throw new ApiError(errorMessage, 'NETWORK_ERROR');
  }
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) => request<T>(endpoint, options),
  post: <T>(endpoint: string, body: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body),
      ...options,
    }),
  put: <T>(endpoint: string, body: unknown, options?: RequestOptions) =>
    request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body),
      ...options,
    }),
  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { method: 'DELETE', ...options }),
};

export { ApiError };
